<template>
  <div class="step">
    <v-autocomplete
      :label="$t('fields.nationality')"
      v-model="country"
      :items="nationalities"
      item-text="name"
      item-value="id"
      return-object
      filled
    ></v-autocomplete>
    <v-alert type="warning" color="warning-text" text class="warning" icon="mdi-alert-outline" v-if="documentExpired === true">
      {{ $t('texts.document_expired') }}
    </v-alert>
    <app-dropdown
      :label="$t('fields.document_type')"
      :value="documentType"
      @change="(value) => documentType = value"
      :items="allowedDocumentTypes"
      :disabled="!country"
      filled />
    <v-alert type="warning" color="warning-text" text class="warning" icon="mdi-alert-outline" v-if="documentError && !documentExpired">
      {{ $t(`texts.document_scan_failed.${documentError}`) }}
    </v-alert>
    <div class="checkin-document-photo">
      <v-btn @click="openPhotoTipsModal('fileFrontSidePhoto')" :disabled="documentType === null" color="primary darken-1" height="48">
        <v-icon left>mdi-camera</v-icon> {{ $t('actions.front_side_photo') }}
      </v-btn>
      <div class="checkin-document-photo-preview" v-if="documentImageFrontURL !== null">
        <div @click="documentImageFrontPopup = true" class="checkin-document-photo-preview-image">
          <img :src="documentImageFrontURL" height="46" />
        </div>
        <div class="checkin-document-photo-preview-success">
          <v-icon color="success">mdi-check-circle-outline</v-icon>
        </div>
      </div>
      <input id="fileFrontSidePhoto" @change="onFileFrontSidePhotoChange" type="file" accept="image/*" capture="camera" />
      <div class="checkin-document-photo-preview-popup" v-if="documentImageFrontPopup === true">
        <v-icon @click="documentImageFrontPopup = false" color="white" class="checkin-document-photo-preview-popup-close">mdi-close</v-icon>
        <img :src="documentImageFrontURL" />
      </div>
    </div>
    <div class="checkin-document-photo" v-if="documentType === 'ID_CARD'">
      <v-btn @click="openPhotoTipsModal('fileBackSidePhoto')" color="primary darken-1" height="48">
        <v-icon left>mdi-camera</v-icon> {{ $t('actions.back_side_photo') }}
      </v-btn>
      <div class="checkin-document-photo-preview" v-if="documentImageBackURL !== null">
        <div @click="documentImageBackPopup = true" class="checkin-document-photo-preview-image">
          <img :src="documentImageBackURL" height="46" />
        </div>
        <div class="checkin-document-photo-preview-success">
          <v-icon color="success">mdi-check-circle-outline</v-icon>
        </div>
      </div>
      <input id="fileBackSidePhoto" @change="onFileBackSidePhotoChange" type="file" accept="image/*" capture="camera" />
      <div class="checkin-document-photo-preview-popup" v-if="documentImageBackPopup === true">
        <v-icon @click="documentImageBackPopup = false" color="white" class="checkin-document-photo-preview-popup-close">mdi-close</v-icon>
        <img :src="documentImageBackURL" />
      </div>
    </div>
    <image-crop-modal :base64Image="croppingImage.image" :triggerOpen="croppingImage.open" @change="setCroppedImage" :isCard="documentType === 'ID_CARD'"/>
    <v-dialog scrollable v-model="photoTipsModal.open" width="300">
      <v-card>
        <v-card-text class="pt-5">
          <v-layout row justify-center>
            <card-image />
          </v-layout>
          <ul class="subtitle-1 mt-5 grey--text">
            <li>
              {{$t('document_scan_tips.tip_1')}}
            </li>
            <li>
              {{$t('document_scan_tips.tip_2')}}
            </li>
            <li>
              {{$t('document_scan_tips.tip_3')}}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="uploadPhoto()" class="primary-gradient" block color="primary" outlined>{{ $t('actions.upload_photo') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { documentTypes } from '@/config'
import i18nCountries from 'i18n-iso-countries'
import { mapState } from 'vuex'
i18nCountries.registerLocale(require('i18n-iso-countries/langs/en.json'))
i18nCountries.registerLocale(require('i18n-iso-countries/langs/es.json'))
export default {
  props: {
    id: String,
  },
  components: {
    CardImage: () => import('@/../public/id-card-tips.svg'),
    AppDropdown: () => import('@/components/app-components/AppDropdown.vue'),
    ImageCropModal: () => import('./ImageCropModal.vue'),
  },
  data () {
    return {
      country: null,
      countries: i18nCountries
        .getNames((navigator.language || navigator.userLanguage).split('-')[0] === 'es' ? 'es' : 'en'),
      documentType: null,
      documentTypes,
      documentImageFront: null,
      documentImageBack: null,
      documentImageFrontURL: null,
      documentImageBackURL: null,
      documentImageFrontPopup: false,
      documentImageBackPopup: false,
      photoTipsModal: {
        open: false,
        photoSide: null,
      },
      croppingImage: {
        open: null,
        image: null,
      },
    }
  },
  computed: {
    documentError () {
      return this.scannedDocument && this.scannedDocument.status === 'FAILED' ? this.scannedDocument.errorCode : null
    },
    documentExpired () {
      return this.scannedDocument && this.scannedDocument.status === 'FAILED' && this.scannedDocument.errorCode === 'INVALID_EXPIRATION_DATE'
    },
    documentDataConfiguration () {
      if (!this.registrationCardConfiguration) {
        return null
      }
      const documentData = this.registrationCardConfiguration.steps.find(step => step.type === 'DOCUMENT_DATA')
      return documentData ? documentData.configuration : null
    },
    allowedDocumentTypes () {
      if (!this.documentDataConfiguration) {
        return []
      }
      if (!this.country) {
        return []
      }
      return documentTypes.filter((type) => {
        if (!this.documentDataConfiguration[type.allowedKey]) {
          return false
        }
        if (this.documentDataConfiguration[type.allowedKey].includes('all')) {
          return true
        }
        return this.documentDataConfiguration[type.allowedKey].includes(this.country.id)
      })
        .map(({ id }) => ({ text: this.$t(`document_type.${ id }`), value: id }))
    },
    nationalities () {
      return Object.keys(this.countries)
        .map((key) => { return { id: i18nCountries.alpha2ToAlpha3(key).toLowerCase(), name: this.countries[key] } })
    },
    isCompleted () {
      if (this.documentImageFront !== null && (this.documentType === 'PASSPORT' || (this.documentType === 'ID_CARD' && this.documentImageBack !== null))) {
        return true
      } else {
        return false
      }
    },
    ...mapState({
      scannedDocument: state => state.registrationCards.scannedDocument,
      registrationCardConfiguration: state => state.service.configuration ? state.service.configuration.registrationCardConfiguration : null,
    }),
  },
  watch: {
    country (newVal) {
      if (this.newVal && this.documentDataConfiguration && this.documentType) {
        const type = documentTypes.find((t) => { return t.id === this.documentType })
        if (!this.documentDataConfiguration[type.allowedKey].includes('all') && !this.documentDataConfiguration[type.allowedKey].includes(newVal.id)) {
          this.documentType = null
        }
      }
    },
    isCompleted: function (newValue, oldValue) {
      this.$emit('onIsCompletedChange', newValue)
    },
    scannedDocument: function (newValue, oldValue) {
      if (newValue !== undefined && newValue !== null) {
        let self = this
        this.country = { id: newValue.countryCode }

        if (newValue.scannedDocumentData !== null) {
          this.documentType = newValue.scannedDocumentData.documentType
        } else if (newValue.documentType !== null) {
          this.documentType = newValue.documentType
        }

        if (newValue.documentFrontImageFileId !== null) {
          this.$store.dispatch('registrationCards/getFile', { guestId: this.id, fileId: newValue.documentFrontImageFileId }).then(file => {
            self.documentImageFrontURL = file
          })
        }

        if (newValue.documentBackImageFileId !== null) {
          this.$store.dispatch('registrationCards/getFile', { guestId: this.id, fileId: newValue.documentBackImageFileId }).then(file => {
            self.documentImageBackURL = file
          })
        }
      }
    },
  },
  methods: {
    openPhotoTipsModal (photoSide) {
      this.photoTipsModal.photoSide = photoSide
      this.photoTipsModal.open = true
    },
    uploadPhoto () {
      document.getElementById(this.photoTipsModal.photoSide).click()
      this.photoTipsModal.photoSide = null
      this.photoTipsModal.open = false
    },
    onFileFrontSidePhotoChange (e) {
      const file = e.target.files[0]
      if (file) {
        this.documentImageFront = file
        this.documentImageFrontURL = URL.createObjectURL(file)
        this.croppingImage.image = this.documentImageFrontURL
        this.croppingImage.open = new Date()
      }
    },
    onFileBackSidePhotoChange (e) {
      const file = e.target.files[0]
      if (file) {
        this.documentImageBack = file
        this.documentImageBackURL = URL.createObjectURL(file)
        this.croppingImage.image = this.documentImageBackURL
        this.croppingImage.open = new Date()
      }
    },
    setCroppedImage ({ file, dataURL }) {
      if (this.documentImageFrontURL === this.croppingImage.image) {
        this.documentImageFront = file
        this.documentImageFrontURL = dataURL
      } else {
        this.documentImageBack = file
        this.documentImageBackURL = dataURL
      }
    },
  },
}
</script>

<style lang="scss">
.checkin-document-photo {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: 8px;

  .v-btn {
    float: left;
    width: 210px;
    margin-right: 12px;
    margin-bottom: 12px;

    .v-btn__content {
      font-size: 14px !important;
      justify-content: right !important;

      .v-icon {
        padding-right: 12px !important;
        padding-left: 12px !important;
      }
    }
  }

  .checkin-document-photo-preview {
    float: left;
    margin-bottom: 12px;

    .checkin-document-photo-preview-image {
      float: left;
      height: 48px;
      border: double 1px transparent;
      background: linear-gradient(white, white), linear-gradient(225deg, var(--v-primary-lighten1), var(--v-primary-darken1));
      background-origin: border-box;
      background-clip: content-box, border-box;
      border-radius: 10px;
      margin-right: 12px;

      img {
        border-radius: 10px !important;
      }
    }

    .checkin-document-photo-preview-success {
      float: left;
      padding-top: 12px;
    }
  }

  .checkin-document-photo-preview-popup {
    position: fixed;
    top: 0;
    left: 0;
    width: calc(100vw);
    height: calc(100vh);
    background-color: rgba(0, 0, 0, 0.61);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;

    .checkin-document-photo-preview-popup-close {
      position: absolute;
      top: 70px;
      right: 15px;
    }

    img {
      border-radius: 10px;
      max-width: 400px;
      height: auto;
      width: 100%;
      padding: 50px;
    }
  }

  input[type="file"] {
    display: none;
  }
}
</style>
